import { useEffect, useState } from "react";
import api from "@/lib/api";
import EmailNav from "./EmailNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Trash2, Pencil, Copy, FileText, Eye } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", subject: "", preview_text: "", body_html: "", category: "general", brand: "any" };

const STARTERS = [
  {
    name: "Field Rental Promo",
    category: "promo",
    subject: "Book your field time at Riverview Athletics — limited slots!",
    preview_text: "Reserve your team's training time today",
    body_html: `<div style="font-family:Arial,sans-serif;color:#1f2937;line-height:1.6;">
<h2 style="color:#EAB308;margin:0 0 16px;">Hey {{first_name}},</h2>
<p>Field time is filling up fast for the upcoming season. Whether you're prepping for tryouts, hosting a tournament, or running team practices — we've got you covered at <strong>Riverview Athletics</strong>.</p>
<ul>
  <li>Indoor turf & outdoor field options</li>
  <li>Hourly or full-day rentals</li>
  <li>Locker rooms & on-site parking</li>
</ul>
<p style="margin:24px 0;text-align:center;">
  <a href="https://rentalsandevents.com/inquire?type=rental" style="display:inline-block;padding:14px 32px;background:#EAB308;color:#fff;text-decoration:none;border-radius:8px;font-weight:600;">Reserve a Field</a>
</p>
<p>See you on the turf,<br/>The Riverview Athletics Team</p></div>`,
  },
  {
    name: "Birthday Party Reminder",
    category: "promo",
    subject: "Make their birthday unforgettable at Rolling River 🎉",
    preview_text: "Indoor turf, snacks, and 90 minutes of pure fun",
    body_html: `<div style="font-family:Arial,sans-serif;color:#1f2937;line-height:1.6;">
<h2 style="color:#3B82F6;margin:0 0 16px;">Hi {{first_name}},</h2>
<p>Looking for a stress-free birthday party kids actually love? Our indoor party package at <strong>Rolling River Day Camp</strong> includes:</p>
<ul>
  <li>1 hour of supervised activities on the turf</li>
  <li>30 minutes for pizza & cake in our dedicated party room</li>
  <li>Festive paper goods and a host to run the show</li>
</ul>
<p style="margin:24px 0;text-align:center;">
  <a href="https://rentalsandevents.com/inquire?type=birthday_party" style="display:inline-block;padding:14px 32px;background:#3B82F6;color:#fff;text-decoration:none;border-radius:8px;font-weight:600;">Check Availability</a>
</p>
<p>Hope to celebrate with you,<br/>Rolling River Day Camp</p></div>`,
  },
  {
    name: "Holiday Greeting",
    category: "greeting",
    subject: "Happy Holidays from our family to yours",
    preview_text: "A note of thanks from Rolling River & Riverview Athletics",
    body_html: `<div style="font-family:Arial,sans-serif;color:#1f2937;line-height:1.7;">
<h2 style="margin:0 0 16px;">Dear {{first_name}},</h2>
<p>As another year comes to a close, we want to thank you for being part of our community. Whether you celebrated a birthday with us, hosted a tournament, or simply stopped by — your trust means the world.</p>
<p>Wishing you a healthy, joyful holiday season and a wonderful new year.</p>
<p>With gratitude,<br/>The Rolling River & Riverview Athletics Team</p></div>`,
  },
];

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [previewing, setPreviewing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/email/templates")
      .then(({ data }) => setTemplates(data.templates || []))
      .catch(() => toast.error("Failed to load templates"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.subject || !form.body_html) return toast.error("Name, subject, and body are required");
    try {
      if (editing) await api.patch(`/email/templates/${editing.id}`, form);
      else await api.post("/email/templates", form);
      toast.success("Saved");
      setShowEditor(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/email/templates/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const open = (t) => {
    if (t) { setEditing(t); setForm({ ...EMPTY, ...t }); }
    else { setEditing(null); setForm(EMPTY); }
    setShowEditor(true);
  };

  const seedStarter = async (s) => {
    try { await api.post("/email/templates", { ...s, brand: "any" }); toast.success("Added starter template"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="templates-page">
      <EmailNav />
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">Templates</h2>
          <p className="text-xs text-muted-foreground">Reusable email designs you can pick when creating a campaign.</p>
        </div>
        <Button size="sm" onClick={() => open(null)} data-testid="templates-new-btn">
          <Plus size={14} className="mr-2" /> New Template
        </Button>
      </div>

      {!loading && templates.length === 0 && (
        <Card className="mb-4">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-2">Start with a pre-built template</h3>
            <p className="text-xs text-muted-foreground mb-4">Pick one of these starters — you can customize after.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {STARTERS.map((s) => (
                <Card key={s.name} className="border-dashed">
                  <CardContent className="p-4">
                    <Badge variant="outline" className="mb-2 text-[10px]">{s.category}</Badge>
                    <h4 className="font-medium text-sm">{s.name}</h4>
                    <p className="text-xs text-muted-foreground mt-1 mb-3">{s.preview_text}</p>
                    <Button size="sm" variant="outline" onClick={() => seedStarter(s)} data-testid={`starter-add-${s.name.replace(/\s+/g,"-").toLowerCase()}`}>Use this starter</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map((t) => (
            <Card key={t.id} data-testid={`template-card-${t.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText size={16} className="text-primary shrink-0" />
                    <h3 className="font-semibold text-sm truncate">{t.name}</h3>
                  </div>
                  <Badge variant="outline" className="text-[10px] capitalize shrink-0">{t.category}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mb-1"><span className="text-foreground/70">Subject:</span> {t.subject}</p>
                {t.preview_text && <p className="text-xs text-muted-foreground italic mb-3 line-clamp-1">{t.preview_text}</p>}
                <div className="flex gap-1">
                  <Button data-testid={`template-preview-${t.id}`} variant="ghost" size="sm" onClick={() => setPreviewing(t)}><Eye size={13} /></Button>
                  <Button data-testid={`template-edit-${t.id}`} variant="ghost" size="sm" onClick={() => open(t)}><Pencil size={13} /></Button>
                  <Button data-testid={`template-delete-${t.id}`} variant="ghost" size="sm" onClick={() => remove(t.id)}>
                    <Trash2 size={13} className="text-rose-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Editor */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Template" : "New Template"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="space-y-3">
              <Input placeholder="Template name" data-testid="tmpl-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="promo">Promo</SelectItem>
                  <SelectItem value="announcement">Announcement</SelectItem>
                  <SelectItem value="greeting">Greeting</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="Subject line" data-testid="tmpl-form-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              <Input placeholder="Preview text (shown in inbox)" value={form.preview_text} onChange={(e) => setForm({ ...form, preview_text: e.target.value })} />
              <Select value={form.brand} onValueChange={(v) => setForm({ ...form, brand: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any brand</SelectItem>
                  <SelectItem value="rrdc">Rolling River</SelectItem>
                  <SelectItem value="riverview">Riverview</SelectItem>
                </SelectContent>
              </Select>
              <textarea
                data-testid="tmpl-form-body"
                className="w-full h-72 rounded-md border border-border bg-background p-3 font-mono text-xs"
                placeholder="HTML body... use {{first_name}} for personalization"
                value={form.body_html}
                onChange={(e) => setForm({ ...form, body_html: e.target.value })}
              />
              <p className="text-[11px] text-muted-foreground">Merge tags: <code>{"{{first_name}}"}</code>, <code>{"{{last_name}}"}</code>, <code>{"{{email}}"}</code></p>
            </div>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Live preview</p>
              <div className="border border-border rounded-md p-3 bg-white text-black h-[480px] overflow-auto">
                <div dangerouslySetInnerHTML={{ __html: form.body_html.replace(/\{\{first_name\}\}/g, "Jamie").replace(/\{\{last_name\}\}/g, "Carter").replace(/\{\{email\}\}/g, "jamie@example.com") }} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
            <Button onClick={save} data-testid="tmpl-form-save">Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview-only */}
      <Dialog open={!!previewing} onOpenChange={() => setPreviewing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{previewing?.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground"><span className="text-foreground/70">Subject:</span> {previewing?.subject}</p>
          {previewing?.preview_text && <p className="text-xs text-muted-foreground italic">{previewing.preview_text}</p>}
          <div className="border border-border rounded-md p-3 bg-white text-black mt-3 max-h-[60vh] overflow-auto">
            <div dangerouslySetInnerHTML={{ __html: (previewing?.body_html || "").replace(/\{\{first_name\}\}/g, "Jamie").replace(/\{\{last_name\}\}/g, "Carter") }} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
